import React, { useEffect, useMemo, useState } from "react"
import { GlobalSidebar, SidebarList, Report, Button } from "components"
import { useAppDispatch, useAppSelector, useFeatureFlags } from "hooks"
import { getPayGroup } from "store/PayGroups/payGroup.slice"
import { getBranches } from "store/Branches/branch.slice"
import { getAllLocations } from "store/Location/locationDetails.slice"
import {
	reports,
	Report as ReportInterface,
	reportNameToUrl,
	COMMON_FIELDS,
	reportsToMask,
	REPORT_NAMES,
} from "./report-config"
import { caseConverters, convertDateTimeToUTC } from "config/utils"
import { reportService } from "services/"
import { getCustomers } from "store/Customers/customers.slice"
import { getSDSuppliers } from "store/StaticData/SDSuppliers.slice"
import moment from "moment"
import { DATE_FORMATS, IS_SUPER_ADMIN, Permissions } from "config/constants"
import PdfViewer from "./PdfViewer"
import { useAbac } from "react-abac"
import { getSDEventTypes } from "../../store/StaticData/SDEventTypes.slice"
import { getEmployees } from "store/Employee/detail.slice"
import { FeatureFlagEnums } from "types/featureFlags.type"
import { sanitizeTrainingTrackerPayload } from "./training-tracker.utils"
import { getUniquePosts } from "store/Location/locationPosts.slice"
import { selectEmployeeListSorted } from "store/Employee/detail.slice"

const FILE_TYPES = {
	pdf: {
		header: "application/pdf",
		fileName: "report.pdf",
	},
	excel: {
		header: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		fileName: "report.xlsx",
	},
}

const valuesToAdd = [
	{
		report: REPORT_NAMES.HOURS_SUMMARY_BY_LOCATION,
		values: { groupBySupplier: true },
	},
	{
		report: REPORT_NAMES.EMPLOYEE_BANS_BY_SITE,
		values: { groupBySupplier: true },
	},
	{
		report: REPORT_NAMES.HOURS_WORKED_BY_LOCATION_DUTIES_ONLY,
		values: { groupBySupplier: true },
	},
]

const sanitizeFileNamePart = (value: string) =>
	value
		.trim()
		.replace(/[\\/:*?"<>|]+/g, "")
		.replace(/\s+/g, "-")

const getReportFileExtension = (fileType: string) => (fileType === "excel" ? "xlsx" : fileType)

const buildReportFileName = (reportName: string, payload: any, fileType: string, employees: any[]) => {
	const baseName = caseConverters.pascalCase(reportName)
	const extension = getReportFileExtension(fileType)

	if (reportName !== REPORT_NAMES.TIME_SHEET_HOURS_WORKED_BY_EMPLOYEE_DUTIES_ONLY || fileType !== "pdf") {
		return `${baseName}.${extension}`
	}

	const employeeIds = [
		...(payload.employee && payload.employee !== "no-employee" ? [payload.employee] : []),
		...(Array.isArray(payload.employees) ? payload.employees : []),
	]
	const uniqueEmployeeIds = [...new Set(employeeIds)]
	const employeeNames = uniqueEmployeeIds
		.map(employeeId => employees.find(employee => employee._id === employeeId))
		.filter(Boolean)
		.map(employee => `${employee.firstName} ${employee.lastName}`)

	const monthName = payload.startDate ? moment(payload.startDate).format("MMMM-YYYY") : ""
	const fileNameParts = [baseName, monthName, ...employeeNames].map(sanitizeFileNamePart).filter(Boolean)

	return `${fileNameParts.join("-")}.${extension}`
}

const Index = () => {
	const dispatch = useAppDispatch()
	const [loading, setLoading] = useState(false)
	const { userHasPermissions } = useAbac()
	const { featureFlagEnabled, featureFlagsLoading } = useFeatureFlags()
	const isSuperAdmin = userHasPermissions(IS_SUPER_ADMIN)
	const shouldViewPayroll = userHasPermissions(Permissions.VIEW_PAYROLL)
	const [selectedReport, setSelectedReport] = useState<ReportInterface | null>(null)
	const [pdf, setPdf] = useState<Blob | null>(null)
	const [downloadReport, setDownloadReport] = useState<{ fileURL: string; fileName: string } | null>(null)
	const employees = useAppSelector(selectEmployeeListSorted())

	const reportsList = useMemo(() => {
		return reports
			.filter(
				item =>
					(item.perform ? userHasPermissions(item.perform) : true) && featureFlagEnabled(item.featureName),
			)
			.map(item => ({ _id: item.name, name: item.title }))
	}, [shouldViewPayroll, featureFlagsLoading, reports])

	useEffect(() => {
		dispatch(getPayGroup())
		dispatch(getBranches(false))
		dispatch(getSDSuppliers(false))
		dispatch(getEmployees())
		dispatch(getAllLocations())
		dispatch(getCustomers())
		dispatch(getSDEventTypes())
		dispatch(getUniquePosts())
	}, [dispatch])

	const handleSelected = (reportName: string | null): void => {
		const selectedReport = reports.find(report => report.name === reportName)
		if (selectedReport) {
			if (reportsToMask.includes(selectedReport?.name)) {
				const { radioButtons } = selectedReport
				const newRadioButtons = radioButtons.map(({ title, fields }) => ({
					title,
					fields: [
						...fields,
						...(isSuperAdmin && featureFlagEnabled(FeatureFlagEnums.IS_MASK_EMPLOYEE_ALLOWED)
							? [COMMON_FIELDS.EMPLOYEE]
							: []),
					],
				}))
				const updatedReport = { ...selectedReport, radioButtons: newRadioButtons }
				setSelectedReport(updatedReport)
			} else {
				setSelectedReport(selectedReport)
			}
		}
	}

	const downloadFile = (fileUrl: string, fileName: string) => {
		const tempLink = document.createElement("a")
		tempLink.href = fileUrl
		tempLink.download = fileName
		tempLink.setAttribute("download", fileName)
		document.body.appendChild(tempLink)
		tempLink.click()
		document.body.removeChild(tempLink)
	}

	const downloadFileFromBlob = (data: Blob, fileType: string, name?: string) => {
		setLoading(false)
		const { header, fileName } = FILE_TYPES[fileType as keyof typeof FILE_TYPES]

		const blobData = new Blob([data], { type: header })

		if (fileType !== "pdf") {
			const fileURL = window.URL.createObjectURL(blobData)
			downloadFile(fileURL, name || fileName)
		} else {
			const fileURL = window.URL.createObjectURL(blobData)
			setDownloadReport({ fileURL, fileName: name || fileName })
			setPdf(blobData)
		}
	}

	const handleCreateReport = async (name: string, data: any, additionalFields?: any, cb = () => undefined) => {
		let payload = {
			...data,
			startDate: moment(data.startDate)
				.set({ hour: 0, minute: 0, second: 0 })
				.format(DATE_FORMATS.YYYY_MM_DD_T_HH_MM_SS1),
			endDate: convertDateTimeToUTC(moment(data.endDate))
				.set({ hour: 23, minute: 59, second: 59 })
				.format(DATE_FORMATS.YYYY_MM_DD_T_HH_MM_SS1),
		}
		if (additionalFields) {
			Object.entries(valuesToAdd.find(value => value.report === name)?.values || {})
				.filter(propName => additionalFields.some((field: string) => field === propName[0].toString()))
				.forEach(array => (payload[array[0]] = array[1]))
		}
		setLoading(true)
		payload = Object.fromEntries(
			Object.entries(payload).map(([key, value]) => [caseConverters.camelCase(key), value]),
		)
		if (name === REPORT_NAMES.TRAINING_TRACKER) {
			payload = sanitizeTrainingTrackerPayload(payload)
		}
		try {
			const res = await reportService.generateReport(reportNameToUrl[name], payload)
			downloadFileFromBlob(res.data, payload.type, buildReportFileName(name, payload, payload.type, employees))
		} catch (error) {
			setLoading(false)
		}

		cb()
	}

	const handleDownloadPdf = () => {
		if (downloadReport) downloadFile(downloadReport.fileURL, downloadReport?.fileName)
	}

	return (
		<div>
			<GlobalSidebar>
				<SidebarList
					title={"Report"}
					selected={selectedReport?.name || ""}
					setSelected={handleSelected}
					list={reportsList}
				/>
			</GlobalSidebar>

			<div className="px-4">
				{pdf && (
					<div className="mx-auto w-full flex-row items-center justify-center">
						<PdfViewer pdf={pdf} setPdf={setPdf} handleDownloadPdf={handleDownloadPdf} />
						<div className="mt-10 flex flex-row-reverse ">
							<Button onClick={() => setPdf(null)}>Dismiss</Button>
						</div>
					</div>
				)}
				{selectedReport && <Report report={selectedReport} onSubmit={handleCreateReport} loading={loading} />}
			</div>
		</div>
	)
}

export default Index
